import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import loginBgMobile from "@/assets/mobile/loginBg.png";
import loginLogo from "@/assets/mobile/loginLogo.png";
import loginBg from "@/assets/home/login.webp";
import LineLeft from "@/assets/home/LineLeft.png";
import LineRight from "@/assets/home/LineRight.png";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Logo from "@/assets/home/logo.svg?react";
import { api } from "@/api/client";
import AuthService from "@/services/auth";
import { useGlobalStore } from "@/store/store";
import { envStorage } from "@/utils/envCache";
import { toast } from "sonner";

import { useIsMobile } from "@/hooks/use-mobile";
import { useTheme } from "@/contexts/ThemeContext";

// 定义组件接收的属性
interface StepLoginDialogProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  onLoginSuccess?: (data: { email: string; team: string; password: string }) => void;
  mode?: 'dialog' | 'page';
}

// 团队类型定义
interface Team {
  teamId: number;
  teamCode: string;
  teamName: string;
  role: string;
  nickname: string;
}

export default function StepLoginDialog({ isOpen, onOpenChange, onLoginSuccess, mode: initialMode }: StepLoginDialogProps) {
  // 步骤管理：1=邮箱 2=团队 3=密码
  const [step, setStep] = useState(1);
  // 表单数据
  const [email, setEmail] = useState("");
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [password, setPassword] = useState("");
  // 错误提示
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // 用于控制输入框校验动画的状态
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const mode = initialMode || (isMobile ? 'page' : 'dialog');
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";

  // 滚动容器引用
  const scrollRef = useRef<HTMLDivElement>(null);
  // 输入框引用
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // 当弹窗关闭时重置状态
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  // 重置所有状态
  const resetState = () => {
    setStep(1);
    setEmail("");
    setSelectedTeam(null);
    setPassword("");
    setError("");
    // 重置错误状态
    setEmailError(false);
    setPasswordError(false);
  };

  // 状态管理
  const [dataTeam, setData] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);

  // 获取团队数据
  const getList = async (email: string) => {
    try {
      setLoading(true);
      const response: any = await api.get("/v1/user/team/query", {
        params: { email },
      });
      console.log(response);
      setData(response);
      if (response.length === 1) {
        setSelectedTeam(response[0]);
        setStep(3);
      } else {
        setStep(2);
      }
    } catch (err) {
      console.error(err);
      console.error("该邮箱并未加入任何团队:", err);
      setError(`${err.message}`);
      // 触发邮箱输入框错误状态
      setEmailError(true);
      // 自动清除错误状态（用于动画重置）
      // setTimeout(() => setEmailError(false), 1000);
    } finally {
      setLoading(false);
    }
  };

  // 团队选择处理
  const handleTeamSelect = (team: Team) => {
    setSelectedTeam(team);
  };

  // 滑动团队列表
  const handleSlide = (direction: "left" | "right") => {
    if (!scrollRef.current) {
      console.warn("滚动容器未找到");
      return;
    }

    // 计算每次滑动距离（容器宽度的50%）
    const scrollAmount =
      direction === "right" ? scrollRef.current.clientWidth * 0.5 : -scrollRef.current.clientWidth * 0.5;

    // 计算目标位置并添加平滑动画
    scrollRef.current.scrollTo({
      left: scrollRef.current.scrollLeft + scrollAmount,
      behavior: "smooth",
    });
  };

  // 下一步
  const handleNext = () => {
    setError("");
    // 重置错误状态
    setEmailError(false);
    setPasswordError(false);

    if (step === 1) {
      // 邮箱校验
      if (!email.trim()) {
        setError("请输入邮箱");
        // 触发邮箱错误状态
        setEmailError(true);
        setTimeout(() => setEmailError(false), 1000);
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        setError("请输入有效的邮箱地址");
        // 触发邮箱错误状态
        setEmailError(true);
        setTimeout(() => setEmailError(false), 1000);
        return;
      }
      getList(email.trim());
    } else if (step === 2) {
      // 团队选择校验
      if (!selectedTeam) {
        setError("请选择一个团队");
        return;
      }
      setStep(3);
    }
  };

  // 上一步
  const handlePrev = () => {
    setError("");
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const setUser = useGlobalStore((state) => state.setUser);
  // 处理登录
  const handleLogin = async () => {
    setError("");
    // 重置密码错误状态
    setPasswordError(false);

    if (!password.trim()) {
      setError("请输入密码");
      // 触发密码错误状态
      setPasswordError(true);
      // setTimeout(() => setPasswordError(false), 1000);
      return;
    }

    // 登录数据
    const loginData: any = {
      email,
      teamId: selectedTeam?.teamId, // 只传递团队ID
      password,
    };

    try {
      const res = await AuthService.login(loginData);

      // 调用父组件传入的登录成功回调
      onLoginSuccess?.(loginData);

      console.log(res);
      if (res) {
        if (res.role === "ADMIN" || res.role === "ROOT") {
          envStorage.setItem("admin", "true");
        } else {
          envStorage.setItem("admin", "false");
        }
        // 保存 teamId
        if (selectedTeam?.teamId !== undefined && selectedTeam?.teamId !== null) {
          envStorage.setItem("teamId", String(selectedTeam.teamId));
        } else if (res.teamId !== undefined && res.teamId !== null) {
          envStorage.setItem("teamId", String(res.teamId));
        }
        setUser(res);
        AuthService.setUserInfo(res);
        // 登录成功后关闭弹窗
        if (mode === 'dialog') {
          onOpenChange?.(false);
        }
        navigate("/", { replace: true });
        window.location.reload();
        return null;
      }
    } catch (err: any) {
      console.log(err);
      // 处理接口返回的错误信息
      if (err instanceof Error) {
        setError(err.message || "登录失败，请稍后重试");
      } else {
        setError("登录失败，请稍后重试");
      }

      // 如果是密码错误，触发密码输入框的错误动画
      if (err?.status === 400) {
        setPasswordError(true);
        // setTimeout(() => setPasswordError(false), 1000);
      }
    }
  };

  // 忘记密码处理
  const handleForgotPassword = async () => {
    try {
      envStorage.setItem("resetEmail", email);
      if (selectedTeam?.teamId !== undefined && selectedTeam?.teamId !== null) {
        envStorage.setItem("resetTeamId", String(selectedTeam.teamId));
      }
      const response = await api.post("/v1/user/password/forgot", null, {
        params: {
          email: email,
          teamId: selectedTeam?.teamId,
        },
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      toast.success("重置密码链接已发送至您的邮箱，请查收");
    } catch (error: any) {
      console.error("重置发送失败:", error.data.msg);
      toast.error(error.data.msg || "重置发送失败");
    } finally {
      console.log("重置发送完成");
    }
  };

  const renderMobilePage = () => {
    const subtitleClass = isDarkMode ? "text-sm text-[#98A2B3]" : "text-sm text-gray-500";
    const labelClass = isDarkMode ? "text-[#E4E7EC]" : "text-gray-900";
    const helperTextClass = isDarkMode ? "text-xs text-[#98A2B3]" : "text-xs text-gray-500";
    const cardBgClass = isDarkMode
      ? "bg-[#0F172A] text-white shadow-[0_-12px_32px_rgba(5,6,12,0.65)]"
      : "bg-white text-gray-900";
    const themedInputClass = isDarkMode
      ? "bg-[#101828] border-[#1D2939] text-white placeholder:text-[#98A2B3]"
      : "";
    const errorBoxClass = isDarkMode
      ? "p-3 bg-[#3B1212] text-[#FDA29B] rounded-md text-sm mb-4 mt-2"
      : "p-3 bg-red-50 text-red-600 rounded-md text-sm mb-4 mt-2";
    return (
    <div
      className={`h-full flex flex-col flex-1 ${isDarkMode ? "bg-[#05060F] text-white" : "bg-white text-gray-900"}`}
      style={{
        backgroundImage: `url(${loginBgMobile})`,
        backgroundSize: "cover",
        backgroundPosition: "center 20%",
        backgroundRepeat: "no-repeat",
        backgroundColor: isDarkMode ? "#05060F" : "#3c1dba",
      }}>
      {/* 顶部logo区域 */}
      <div className="h-[340px] flex flex-col items-center pt-12 text-white">
        <div className="flex items-center">
          <img src={loginLogo} alt="loginLogo" className="h-8 w-auto object-contain" />
          <span 
            className="text-xl font-bold ml-2 bg-gradient-to-r from-[#00FFDD] to-[#00D4FF] text-transparent bg-clip-text"
          >
            BeeFintech AI
          </span>
        </div>
      </div>

      {/* 登录表单区域 */}
      <div className={`flex-1 flex flex-col p-6 -mt-8 rounded-t-2xl ${cardBgClass}`}>
        <header className="text-left p-0 mb-4">
          <h2 className="text-2xl font-semibold" style={{ display: "flex", alignItems: "center" }}>
            {step > 1 && (
              <div onClick={handlePrev} className="p-2 -ml-2 cursor-pointer">
                <img src={LineLeft} className="w-[26px] h-[26px]" alt="返回" />
              </div>
            )}
            <div>
              {step === 1 && "欢迎登录"}
              {step === 2 && "选择团队"}
              {step === 3 && selectedTeam?.nickname}
            </div>
          </h2>
        </header>

        <div className="flex-1 flex flex-col justify-start ">
          {/* ... content for steps 1, 2, 3 ... */}
          {step === 1 && (
            <>
              <p className={`${subtitleClass} mb-6`}>你身边的智能助手，可以为你答疑解惑</p>
              <div className={labelClass} style={{ marginBottom: "5px" }}>账号</div>
              <Input
                ref={emailInputRef}
                type="email"
                placeholder="请输入邮箱号"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`mt-2 h-12 ${emailError ? "input-error shake" : ""} ${themedInputClass}`}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleNext();
                  }
                }}
              />
            </>
          )}

          {step === 2 && (
            <>
               <p className={`${subtitleClass} mb-6`}>检测到多个团队，请选择登录的团队</p>
               <div className="grid grid-cols-2 gap-4">
                  {dataTeam.map((team) => (
                    <Button
                      key={team.teamId}
                      variant={selectedTeam?.teamId === team.teamId ? "default" : "outline"}
                      onClick={() => handleTeamSelect(team)}
                      className={`h-12 ${selectedTeam?.teamId === team.teamId ? "bg-black text-white hover:bg-gray-800" : ""}`}>
                      {team.teamName}
                    </Button>
                  ))}
                </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="text-sm font-medium mb-1 flex items-center justify-between">
                密码
                <div  className="text-sm font-medium text-primary hover:underline" onClick={handleForgotPassword}>
                  忘记密码?
                </div>
              </div>
              <div className="relative">
                <Input
                  ref={passwordInputRef}
                  type={showPassword ? "text" : "password"}
                  placeholder="请输入密码"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`mt-1 pr-10 h-12 ${passwordError ? "input-error shake" : ""} ${themedInputClass}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleLogin();
                    }
                  }}
                />
                <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-5 w-5 text-gray-500" /> : <Eye className="h-5 w-5 text-gray-500" />}
                </button>
              </div>
            </>
          )}

          {error && <div className={errorBoxClass}>{error}</div>}

        </div>


        <div className="flex flex-col gap-3 mt-6">
          {step < 3 ? (
            <Button onClick={handleNext} className="w-full h-12">
              下一步
            </Button>
          ) : (
            <Button onClick={handleLogin} className="w-full h-12">
              登录
            </Button>
          )}
          {step === 1 && (
            <p className={`${helperTextClass} mt-2 text-center`}>登入代表阁下同意AI Chatbot的《隐私政策》《用户服务协议》</p>
          )}
        </div>
      </div>
    </div>
  );};

  if (mode === 'page') {
    return renderMobilePage();
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[600px]"
        style={{
          width: "750px",
          maxWidth: "750px",
          maxHeight: "423px",
          padding: "0",
        }}>
        {/* 输入框抖动动画样式 */}
        <style dangerouslySetInnerHTML={{__html: `
          @keyframes shake {
            0%,
            100% {
              transform: translateX(0);
            }
            10%,
            30%,
            50%,
            70%,
            90% {
              transform: translateX(-5px);
            }
            20%,
            40%,
            60%,
            80% {
              transform: translateX(5px);
            }
          }
          .shake {
            animation: shake 0.5s ease-in-out;
          }
          .input-error {
            border-color: #ef4444 !important; /* 红色边框 */
          }
        `}}/>

        <div style={{ display: "flex" }}>
          <div
            style={{
              width: "375px",
              maxWidth: "375px",
              backgroundImage: `url(${loginBg})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "center",
              height: "426px",
            }}></div>

          <div style={{ width: "375px", maxWidth: "375px", padding: "1rem" }}>
            <DialogHeader>
              <DialogTitle className="text-2xl font-semibold mt-12" style={{ display: "flex", alignItems: "center" }}>
                {/* 上一步按钮 */}
                {step > 1 && (
                  <div onClick={handlePrev}>
                    <img src={LineLeft} className="w-[26px] h-[26px]" alt="" />
                  </div>
                )}
                <div>
                  {step === 1 && "欢迎登录"}
                  {step === 2 && "选择团队"}
                  {step === 3 && `${selectedTeam.nickname}`}
                </div>
              </DialogTitle>
            </DialogHeader>

            {/* 步骤内容 */}
            <div className="space-y-4 py-2">
              {step === 1 && (
                <>
                  <p className="text-sm text-gray-500">你身边的智能助手，可以为你答疑解惑</p>

                  <div style={{ marginBottom: "5px" }}>账号</div>
                  <Input
                    ref={emailInputRef}
                    type="email"
                    placeholder="请输入邮箱号"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    // 错误样式和动画
                    className={`mt-2 ${emailError ? "input-error shake" : ""}`}
                    autoFocus
                    // 添加回车事件处理
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleNext();
                      }
                    }}
                  />
                </>
              )}

              {step === 2 && (
                <>
                  {/* 全局滚动条样式 */}
                  <style
                    dangerouslySetInnerHTML={{
                      __html: `
                                        .team-scroll-container::-webkit-scrollbar {
                                            display: none;
                                        }
                                        .team-scroll-container {
                                            -ms-overflow-style: none;
                                            scrollbar-width: none;
                                        }
                                    `,
                    }}
                  />

                  <div className="space-y-4 py-2">
                    <p className="text-sm text-gray-500">检测到多个团队，请选择登录的团队</p>

                    <div style={{ display: "flex", alignItems: "center" }}>
                      <div onClick={() => handleSlide("left")} className="h-4 w-4">
                        <img src={LineLeft} className="w-[15px] h-[15px]" alt="" />
                      </div>
                      {/* 滚动容器 */}
                      <div
                        ref={scrollRef}
                        className="team-scroll-container overflow-x-auto w-full"
                        style={{
                          maxHeight: "120px",
                          padding: "4px 0",
                          width: "90%",
                          boxSizing: "border-box",
                        }}>
                        <div className="flex space-x-3 px-1" style={{ minWidth: "max-content" }}>
                          {dataTeam.map((team) => (
                            <Button
                              key={team.teamId}
                              variant={selectedTeam?.teamId === team.teamId ? "default" : "outline"}
                              onClick={() => handleTeamSelect(team)}
                              className={`w-32 h-10 flex-shrink-0 ${
                                selectedTeam?.teamId === team.teamId ? "bg-black text-white hover:bg-gray-800" : ""
                              }`}>
                              {team.teamName}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div onClick={() => handleSlide("right")} className="h-4 w-4">
                        <img src={LineRight} className="w-[15px] h-[15px]" alt="" />
                      </div>
                    </div>
                  </div>
                </>
              )}

              {step === 3 && (
                <>
                  <p className="text-sm text-gray-500">{/*请准确无误的输入您的密码*/}</p>
                  <div className="mt-2">
                    <div className="text-sm font-medium mb-1 flex items-center justify-between">
                      密码
                      <a
                        href="#"
                        className="text-sm font-medium text-primary hover:underline"
                        onClick={handleForgotPassword}>
                        忘记密码?
                      </a>
                    </div>
                    <div className="relative">
                      <Input
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        placeholder="请输入密码"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        // 错误样式和动画
                        className={`mt-1 pr-10 ${passwordError ? "input-error shake" : ""}`}
                        // 添加回车事件处理
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleLogin();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-500" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* 错误提示 */}
            {error && <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm mb-4">{error}</div>}

            {/* 操作按钮 */}
            <div className="flex justify-end gap-3 mt-6">
              {step < 3 ? (
                <Button onClick={handleNext} style={{ width: "100%" }}>
                  下一步
                </Button>
              ) : (
                <Button onClick={handleLogin} size="sm" style={{ width: "100%" }}>
                  登录
                </Button>
              )}
            </div>

            {step === 1 && (
              <p className="text-xs text-gray-500 mt-5">登入代表阁下同意AI Chatbot的《隐私政策》《用户服务协议》</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
